import React, { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
  orderBy,
} from "firebase/firestore";
import { db } from "../../firebase";
import "../HomeScreen.css";
import "./Destinations.css";
import Navbar from "../Navbar";
import Footer from "../Footer";

// ══════════════════════════════════════════════
// LAZY IMAGE (shows gif loader until image is fetched)
// ══════════════════════════════════════════════
function LazyImage({ src, alt, className }) {
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    setLoaded(false);

    // If the browser already has this image cached, the <img> tag can
    // finish loading before React attaches the onLoad listener below —
    // in that case onLoad never fires and the gif stays stuck forever.
    // So we manually check `.complete` right after mount/src-change.
    const el = imgRef.current;
    if (el && el.complete && el.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [src]);

  return (
    <div className="dest-lazy-img-box">
      {!loaded && (
        <img
          src="/assets/images/globegif.gif"
          alt="loading"
          className="dest-lazy-img-gif"
        />
      )}
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        className={className}
        loading="lazy"
        style={{ opacity: loaded ? 1 : 0 }}
        onLoad={() => setLoaded(true)}
        onError={(e) => {
          e.target.style.display = "none";
        }}
      />
    </div>
  );
}

// ══════════════════════════════════════════════
// IMAGE CAROUSEL
// ══════════════════════════════════════════════
function ImageCarousel({ images, name }) {
  const [current, setCurrent] = useState(0);
  if (!images || images.length === 0) {
    return (
      <div className="dest-img-placeholder">
        <span>🌍</span>
      </div>
    );
  }
  return (
    <div className="dest-img-wrapper">
      <LazyImage src={images[current]} alt={name} className="dest-img" />
      {images.length > 1 && (
        <>
          <button
            className="dest-carousel-btn dest-carousel-prev"
            onClick={(e) => {
              e.stopPropagation();
              setCurrent((c) => (c - 1 + images.length) % images.length);
            }}
          >
            ‹
          </button>
          <button
            className="dest-carousel-btn dest-carousel-next"
            onClick={(e) => {
              e.stopPropagation();
              setCurrent((c) => (c + 1) % images.length);
            }}
          >
            ›
          </button>
          <div className="dest-dots">
            {images.map((_, i) => (
              <span
                key={i}
                className={`dest-dot${i === current ? " active" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrent(i);
                }}
              />
            ))}
          </div>
          <div className="dest-img-count">📷 {images.length}</div>
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════
// DESTINATION CARD
// ══════════════════════════════════════════════
function DestinationCard({ dest }) {
  const navigate = useNavigate();
  const location = [dest.state, dest.country].filter(Boolean).join(", ");
  
  return (
    <div
      className="destination-card"
      onClick={() => navigate(`/destinations/${dest.id}`)}
    >
      <ImageCarousel images={dest.imageUrls} name={dest.name} />
      <div className="card-content">
        {location && <p className="dest-location">📍 {location}</p>}
        <h3>{dest.name}</h3>
        {dest.tagline && <p className="dest-tagline">"{dest.tagline}"</p>}
        {dest.bestTimeToVisit && (
          <p className="dest-meta">🗓 Best time: {dest.bestTimeToVisit}</p>
        )}
        {dest.linkedTours?.length > 0 && (
          <p className="dest-meta">
            🗺️ {dest.linkedTours.length} Tour
            {dest.linkedTours.length > 1 ? "s" : ""} Available
          </p>
        )}
        <button className="card-btn">View Details</button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════
const Destinations = () => {
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const fetchAll = async () => {
      try {
        // 1. Fetch all active destinations
        const destQ = query(
          collection(db, "destinations"),
          where("isActive", "==", true)
        );
        const destSnap = await getDocs(destQ);
        const rawDests = destSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        // 2. Collect all unique linkedTourIds across all destinations
        const allTourIds = [
          ...new Set(rawDests.flatMap((d) => d.linkedTourIds || [])),
        ];

        // 3. Fetch those tours in parallel (only active ones)
        const tourMap = {};
        if (allTourIds.length > 0) {
          await Promise.all(
            allTourIds.map(async (tourId) => {
              try {
                const tourDoc = await getDoc(doc(db, "tours", tourId));
                if (tourDoc.exists()) {
                  const tourData = { id: tourDoc.id, ...tourDoc.data() };

                  // Only include if tour is active
                  if (tourData.isActive === true) {
                    // 4. Fetch active packages for this tour
                    const pkgsQ = query(
                      collection(db, "tours", tourId, "packages"),
                      where("isActive", "==", true),
                      orderBy("createdAt", "desc")
                    );
                    const pkgsSnap = await getDocs(pkgsQ);
                    tourData.packages = pkgsSnap.docs.map((p) => ({
                      id: p.id,
                      ...p.data(),
                    }));

                    tourMap[tourId] = tourData;
                  }
                }
              } catch (e) {
                console.warn(`Failed to load tour ${tourId}:`, e);
              }
            })
          );
        }

        // 5. Attach resolved tours to each destination
        const enriched = rawDests.map((dest) => ({
          ...dest,
          linkedTours: (dest.linkedTourIds || [])
            .map((id) => tourMap[id])
            .filter(Boolean), // remove any that weren't active/found
        }));

        setDestinations(enriched);
      } catch (err) {
        console.error("Firestore error: ", err);
        setError(`Error: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  const filtered = destinations.filter((d) => {
    const s = search.toLowerCase();
    return (
      !search ||
      d.name?.toLowerCase().includes(s) ||
      d.country?.toLowerCase().includes(s) ||
      d.state?.toLowerCase().includes(s)
    );
  });

  return (
    <div className="home-container">
      {/* Navbar */}
      <Navbar />
      
      {/* Hero */}
      <section className="hero-section" style={{ height: "50vh" }}>
        <div className="hero-overlay">
          <h1>
            Our <span style={{
              background: "linear-gradient(135deg, #ff8c00, #ff5500)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent"
            }}>Destinations</span>
          </h1>
          <p>Explore the world's most breathtaking locations.</p>
        </div>
      </section>

      {/* Content */}
      <section className="destinations-section">
        <h2>Where Do You Want To Go?</h2>

        <div className="dest-search-wrap">
          <span className="dest-search-icon">🔍</span>
          <input
            placeholder="Search by name, country, state..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {loading ? (
          <div className="dest-skeleton-grid">
            {[1, 2, 3, 4].map((i) => (
              <div className="dest-skeleton" key={i}>
                <div className="dest-skel-img shimmer" />
                <div className="dest-skel-body">
                  <div className="dest-skel-line shimmer" style={{ width: "50%" }} />
                  <div className="dest-skel-line shimmer" style={{ width: "80%" }} />
                  <div className="dest-skel-line shimmer" style={{ width: "60%" }} />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="dest-error">⚠️ {error}</div>
        ) : filtered.length === 0 ? (
          <div className="dest-empty">
            <p>
              No destinations found{search ? ` for "${search}"` : " "}.
            </p>
          </div>
        ) : (
          <div className="destinations-grid">
            {filtered.map((dest) => (
              <DestinationCard
                key={dest.id}
                dest={dest}
              />
            ))}
          </div>
        )}
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Destinations;